import { commercialOfferCatalog } from "./commercial-policy.js";

// Marketing view of the plans. Amounts come from the commercial catalog so the
// page and checkout can never disagree; change prices there, not here.
// Everything else on the pricing cards (names, lists, labels) lives below and is
// safe to edit without touching billing.
export const landingPricing = Object.freeze({
  // While false, paid buttons lead to the free download and say checkout opens
  // at launch. Flip to true once Stripe checkout is approved and live.
  checkoutOpen: false,
  // While false, the plans show without numbers and the page says prices are
  // announced at launch. Flip to true once the amounts in commercial-policy.js
  // are the ones to publish.
  showPrices: false,
  checkoutPath: "/checkout",
  currency: "usd",
  plans: Object.freeze([
    Object.freeze({
      id: "free",
      name: "DittyBag",
      tagline: "The clipboard that remembers.",
      offers: Object.freeze([]),
      features: Object.freeze([
        "Clipboard history with search",
        "Quick History, one shortcut away",
        "Pins and collections",
        "Screenshots and area capture",
        "Works offline, no account",
      ]),
    }),
    Object.freeze({
      id: "pro",
      name: "DittyBag Pro",
      tagline: "Every tool in the bag.",
      badge: "Best value",
      featured: true,
      offers: Object.freeze(["pro_lifetime"]),
      features: Object.freeze([
        "Everything in DittyBag",
        "Image Studio markup, blur and redact",
        "Screen recording and GIF",
        "Grab text from any image (OCR)",
        "Video studio with soundboard",
        "Prompts, snippets and Keybind Center",
        "Use on up to 3 computers",
      ]),
    }),
    Object.freeze({
      id: "pro_plus",
      name: "DittyBag Pro+",
      tagline: "Your bag on every device.",
      offers: Object.freeze(["pro_plus_monthly", "pro_plus_annual"]),
      features: Object.freeze([
        "Everything in Pro",
        "End-to-end encrypted sync",
        "Web History from any browser",
        "Up to 5 devices, 2 GB of synced media",
        "Early access to new tools",
      ]),
    }),
  ]),
});

const intervalLabels = Object.freeze({ month: "/mo", year: "/yr" });

export function formatPrice(cents, currency = landingPricing.currency) {
  const amount = Math.max(0, Number(cents) || 0) / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

// One display row per offer, e.g. { key, price: "$49", period: "once" }.
export function planPrices(plan, catalog = commercialOfferCatalog) {
  return plan.offers
    .map((key) => catalog[key])
    .filter(Boolean)
    .map((offer) => ({
      key: offer.key,
      cents: offer.unitAmountCents,
      price: formatPrice(offer.unitAmountCents, offer.currency),
      period: offer.billing === "one_time" ? "once" : intervalLabels[offer.interval] ?? "",
      interval: offer.interval ?? null,
    }));
}

// Whole months a yearly price saves against paying monthly, for the toggle.
export function annualSavingsMonths(plan, catalog = commercialOfferCatalog) {
  const prices = planPrices(plan, catalog);
  const monthly = prices.find((row) => row.interval === "month");
  const annual = prices.find((row) => row.interval === "year");
  if (!monthly || !annual || monthly.cents <= 0) return 0;
  return Math.max(0, Math.floor((monthly.cents * 12 - annual.cents) / monthly.cents));
}

export function lowestPaidPrice(catalog = commercialOfferCatalog) {
  const pro = landingPricing.plans.find((plan) => plan.id === "pro");
  return pro ? planPrices(pro, catalog)[0] ?? null : null;
}
